import apiClient from '../utils/apiClient';
import { checkNotificationRequired } from '../utils/notificationChecker';
import { logger } from '../utils/debugLogger';
import doctorContractApiService from './doctorContractApiService';
import type {
  MedicalReservationRecord,
  MedicalReservationListResponse,
  MedicalReservationFilterOptions,
  MedicalReservationUpdateRequest,
  BulkStatusUpdateRequest,
  ActionStatus,
  PasteToSpreadsheetRequest,
  PasteToSpreadsheetResponse,
  PasteStatusResponse,
} from '../types/medicalReservation';

/**
 * 診療予約データAPI通信サービス
 * 
 * 意図：
 * - レコードベースでの診療予約データ管理
 * - 個別レコードの詳細表示・更新機能
 * - 一括ステータス更新機能
 * 
 * 注意点：
 * - エラーハンドリングとリトライ処理
 * - レスポンスデータの型安全性
 * - 認証トークンの管理
 * 
 * 改善提案：
 * - リアルタイム更新（WebSocket対応）
 * - オフライン対応とキャッシュ機能
 * - 楽観的更新の実装
 */


/**
 * APIレスポンスのレコードデータを変換
 * スネークケースからキャメルケースへの変換と、CSVデータからの患者名取得
 */
const transformRecordData = (record: any): MedicalReservationRecord => {
  // APIレスポンスのフィールド名に合わせて取得
  const doctorInCharge = record.assignedDoctor || record.doctor_in_charge || record.doctorInCharge || '';
  const patientName = record.nameKanji || record.patient_name || '';
  const anybotId = record.anybotId || record.anybot_id || '';
  const consultationStartTime = record.consultationStartTime || record.consultation_start_time || '';

  // CSVデータの構築
  const csvData: { [key: string]: string } = {};
  
  // 基本情報のマッピング
  if (record.nameKanji) csvData['名前（漢字）'] = record.nameKanji;
  if (record.nameKana) csvData['名前（カナ）'] = record.nameKana;
  if (record.assignedDoctor) csvData['担当医師'] = record.assignedDoctor;
  if (record.anybotId) csvData['anybot ID'] = record.anybotId;
  if (record.consultationStartTime) csvData['診療開始時間'] = record.consultationStartTime;
  if (record.phone) csvData['電話番号'] = record.phone;
  if (record.age !== undefined) csvData['年齢'] = String(record.age);
  if (record.height) csvData['身長'] = record.height;
  if (record.weight) csvData['体重'] = record.weight;
  if (record.status) csvData['ステータス'] = record.status;
  if (record.consultationStyle) csvData['診療スタイル'] = record.consultationStyle;
  if (record.consultationTool) csvData['使用ツール'] = record.consultationTool;
  if (record.consultationRequest) csvData['診療リクエスト'] = record.consultationRequest;
  if (record.questionnaireResponse) csvData['問診回答'] = record.questionnaireResponse;
  if (record.reexamQuestionnaire) csvData['再診用問診'] = record.reexamQuestionnaire;
  if (record.bmiUnder30 !== undefined) csvData['BMIの値は30以下'] = record.bmiUnder30 ? '○' : '×';
  if (record.tags) csvData['tags'] = record.tags;
  if (record.performanceReward) csvData['成果報酬'] = record.performanceReward;
  if (record.counselorSupport !== undefined) csvData['カウンセラー対応'] = record.counselorSupport ? 'あり' : 'なし';
  
  // 要通知判定に必要なフィールドのマッピング（APIレスポンスの新しいフィールドに対応）
  if (record.pregnancyDesire !== undefined) {
    csvData['妊娠希望'] = record.pregnancyDesire ? 'あり' : 'なし';
  }
  if (record.migraineWithAura !== undefined) {
    csvData['前兆のある片頭痛'] = record.migraineWithAura ? 'あり' : 'なし';
  }
  if (record.familyThrombosisHistory !== undefined) {
    csvData['ご家族に血栓症'] = record.familyThrombosisHistory ? 'あり' : 'なし';
  }
  if (record.hypertensionDiagnosis !== undefined) {
    csvData['高血圧診断'] = record.hypertensionDiagnosis ? 'あり' : 'なし';
  }
  if (record.smoking !== undefined) {
    // 本来は「15本/日」などの詳細情報が必要だが、現在はboolean値のみ
    csvData['タバコ'] = record.smoking ? 'あり' : 'なし';
  }
  if (record.pregnancyBirthNursing !== undefined) {
    csvData['妊娠・出産・授乳有無'] = record.pregnancyBirthNursing ? 'あり' : 'なし';
  }
  if (record.otherDiseases) {
    csvData['その他疾患'] = record.otherDiseases;
  }
  if (record.memberRegistration !== undefined) {
    csvData['会員登録'] = record.memberRegistration ? '○' : '未回答';
  }
  
  // その他のフィールドマッピング
  if (record.identityDocument) csvData['身分証'] = record.identityDocument;
  if (record.bloodTestImage) csvData['採血画像'] = record.bloodTestImage;
  if (record.delayInfo) csvData['遅延'] = record.delayInfo;
  if (record.consultationResponse) csvData['診療対応'] = record.consultationResponse;
  if (record.csSupportRequired !== undefined) csvData['CS対応必要有無'] = record.csSupportRequired ? 'あり' : 'なし';
  if (record.doctorToCsCounselor) csvData['医師からCS/カウンセラー'] = record.doctorToCsCounselor;
  if (record.doubleCheckRequired !== undefined) csvData['Wチェック'] = record.doubleCheckRequired ? '○' : '';
  if (record.mederiCsToDoctor) csvData['mederi CSから医師へ'] = record.mederiCsToDoctor;
  if (record.counselorToDoctor) csvData['カウンセラーから医師へ'] = record.counselorToDoctor;
  if (record.parentConsent !== undefined) csvData['親への同意'] = record.parentConsent ? '○' : '×';
  if (record.medicationExperience) csvData['服用経験'] = record.medicationExperience;
  if (record.previousPill) csvData['服用していたピル'] = record.previousPill;
  if (record.improvementPurpose) csvData['改善目的'] = record.improvementPurpose;
  if (record.preferredPill) csvData['希望ピル'] = record.preferredPill;
  if (record.questionToDoctor) csvData['医師に聞きたいこと'] = record.questionToDoctor;
  if (record.reexamQuestionToDoctor) csvData['※再診問診 医師にききたいこと'] = record.reexamQuestionToDoctor;
  if (record.reexamPillChangeRequest) csvData['※再診問診 種類変更希望'] = record.reexamPillChangeRequest;
  if (record.reexamSideEffectMedicineRequest) csvData['※再診問診 副作用緩和薬希望'] = record.reexamSideEffectMedicineRequest;
  if (record.lastMenstruationDate) csvData['前回生理開始日'] = record.lastMenstruationDate;
  if (record.menstrualCycle !== undefined) csvData['月経周期'] = String(record.menstrualCycle);
  if (record.datesToAvoidMenstruation) csvData['生理を避けたい日にち'] = record.datesToAvoidMenstruation;
  if (record.sexualActivityDatetime) csvData['性行為の日時'] = record.sexualActivityDatetime;
  if (record.pregnancyExperience !== undefined) csvData['妊娠経験'] = record.pregnancyExperience ? 'あり' : 'なし';
  if (record.pregnancyBirthInfo) csvData['妊娠時期・出産回数'] = record.pregnancyBirthInfo;
  if (record.pregnancyComplications) csvData['過去妊娠中に「黄疸」「ヘルペス」「持続性掻疼症」と診断されたことはありますか？'] = record.pregnancyComplications;
  if (record.recentSurgery !== undefined) csvData['過去2週間以内に手術'] = record.recentSurgery ? 'あり' : 'なし';
  if (record.currentMedications) csvData['飲んでいるお薬'] = record.currentMedications;
  if (record.allergies) csvData['アレルギー'] = record.allergies;
  if (record.irregularBleeding !== undefined) csvData['不正出血'] = record.irregularBleeding ? 'あり' : 'なし';
  if (record.gynecologicalDiseases) csvData['婦人科系疾患'] = record.gynecologicalDiseases;
  if (record.familyBreastCancerHistory !== undefined) csvData['ご家族に乳がん'] = record.familyBreastCancerHistory ? 'あり' : 'なし';
  if (record.mediumDosePrescription !== undefined) csvData['中用量処方有無'] = record.mediumDosePrescription ? 'あり' : 'なし';
  if (record.morningAfterPillPrescription !== undefined) csvData['アフターピル処方有無'] = record.morningAfterPillPrescription ? 'あり' : 'なし';
  if (record.sideEffectMedicinePrescription !== undefined) csvData['副作用改善薬処方有無'] = record.sideEffectMedicinePrescription ? 'あり' : 'なし';

  const transformedData: MedicalReservationRecord = {
    id: record.id,
    upload_batch_id: record.upload_batch_id || record.uploadBatchId || '',
    doctorInCharge: doctorInCharge,
    anybotId: anybotId,
    patientName: patientName,
    consultationStartTime: consultationStartTime,
    actionStatus: record.actionStatus || record.action_status || 'none',
    identity_verification_flag: record.identity_verification_flag || record.identityVerificationFlag || false,
    paste_flag: record.paste_flag || record.pasteFlag || false,
    businessStatus: record.businessStatus || record.business_status || 'uncompleted',
    assignedTo: record.assignedTo || record.assigned_to,
    notes: record.notes,
    csvData: csvData,
    created_at: record.createdAt || record.created_at || '',
    updated_at: record.updatedAt || record.updated_at || '',
  };

  // 要通知判定を優先的に実行
  // 既存のステータスに関わらず、要通知条件を満たす場合は上書き
  if (checkNotificationRequired(transformedData)) {
    transformedData.actionStatus = 'notification_required';
  }

  return transformedData;
};

/**
 * 診療予約データAPI関数群
 */
export const medicalReservationApiService = {
  /**
   * 診療予約レコード一覧取得
   * @param filters フィルタ・検索・ページング条件
   * @returns 診療予約レコード一覧とメタデータ
   */
  async getMedicalReservations(
    filters?: MedicalReservationFilterOptions
  ): Promise<MedicalReservationListResponse> {
    const params: Record<string, string> = {};
    
    if (filters?.search) params.search = filters.search;
    if (filters?.doctor) params.doctor = filters.doctor;
    if (filters?.actionStatus?.length) {
      params.action_status = filters.actionStatus.join(',');
    }
    if (filters?.dateRange?.start) params.date_start = filters.dateRange.start;
    if (filters?.dateRange?.end) params.date_end = filters.dateRange.end;
    if (filters?.uploadBatchId) params.upload_batch_id = filters.uploadBatchId;
    if (filters?.sort_by) {
      // camelCaseからsnake_caseへの変換
      const sortByMap: Record<string, string> = {
        'consultationStartTime': 'consultation_start_time',
        'doctorInCharge': 'doctor_in_charge',
        'anybotId': 'anybot_id',
        'patientName': 'patient_name',
        'actionStatus': 'action_status',
        'assignedTo': 'assigned_to',
        'createdAt': 'created_at',
        'updatedAt': 'updated_at'
      };
      params.sort_by = sortByMap[filters.sort_by] || filters.sort_by;
    }
    if (filters?.sort_order) params.sort_order = filters.sort_order;
    if (filters?.page) params.page = filters.page.toString();
    if (filters?.page_size) params.page_size = filters.page_size.toString();

    
    try {
      const response = await apiClient.get<any>('/api/medical-reservations', { params });
      
      
      // レスポンスの正規化
      let normalizedResponse: MedicalReservationListResponse;
      
      // successフィールドを持つレスポンスの処理
      if (response.data && typeof response.data === 'object' && 'success' in response.data && response.data.data) {
        // Laravel APIの標準レスポンス形式
        if (typeof response.data.data === 'object' && 'data' in response.data.data) {
          // ネストされたdata構造: { success: true, data: { data: [...], meta: {...} } }
          normalizedResponse = {
            data: Array.isArray(response.data.data.data) ? response.data.data.data : [],
            meta: response.data.data.meta ? {
              current_page: response.data.data.meta.currentPage || response.data.data.meta.current_page || 1,
              total_pages: response.data.data.meta.lastPage || response.data.data.meta.total_pages || 1,
              total_records: response.data.data.meta.totalRecords || response.data.data.meta.total_records || 0,
              page_size: response.data.data.meta.perPage || response.data.data.meta.page_size || 10
            } : {
              current_page: 1,
              total_pages: 1,
              total_records: 0,
              page_size: 10
            }
          };
        } else if (Array.isArray(response.data.data)) {
          // data直下が配列の場合: { success: true, data: [...] }
          normalizedResponse = {
            data: response.data.data,
            meta: response.data.meta || {
              current_page: 1,
              total_pages: 1,
              total_records: response.data.data.length,
              page_size: response.data.data.length
            }
          };
        } else {
          // その他のdata構造
          normalizedResponse = {
            data: [],
            meta: {
              current_page: 1,
              total_pages: 1,
              total_records: 0,
              page_size: 10
            }
          };
        }
      } else if (Array.isArray(response.data)) {
        // 配列が直接返された場合の処理
        normalizedResponse = {
          data: response.data,
          meta: {
            current_page: 1,
            total_pages: 1,
            total_records: response.data.length,
            page_size: response.data.length
          }
        };
      } else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        // dataフィールドを持つオブジェクトの場合: { data: [...], meta: {...} }
        normalizedResponse = {
          data: Array.isArray(response.data.data) ? response.data.data : [],
          meta: response.data.meta || {
            current_page: 1,
            total_pages: 1,
            total_records: 0,
            page_size: 10
          }
        };
      } else {
        // 想定外の形式の場合
        normalizedResponse = {
          data: [],
          meta: {
            current_page: 1,
            total_pages: 1,
            total_records: 0,
            page_size: 10
          }
        };
      }
      
      // データ変換を適用
      normalizedResponse.data = normalizedResponse.data.map(transformRecordData);
      
      return normalizedResponse;
    } catch (error) {
      console.error('❌ 診療予約一覧取得エラー:', error);
      throw error;
    }
  },

  /**
   * 診療予約レコード詳細取得
   * @param id レコードID
   * @returns 診療予約レコード詳細
   */
  async getMedicalReservation(id: string): Promise<MedicalReservationRecord> {
    const response = await apiClient.get<any>(`/api/medical-reservations/${id}`);
    
    // レスポンスの正規化と変換
    let record;
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      // { data: record } 形式の場合
      record = response.data.data;
    } else {
      // 直接レコードが返される場合
      record = response.data;
    }
    
    return transformRecordData(record);
  },

  /**
   * 診療予約レコード更新
   * @param id レコードID
   * @param data 更新データ
   * @returns 更新後のレコード
   */
  async updateMedicalReservation(
    id: string,
    data: MedicalReservationUpdateRequest
  ): Promise<MedicalReservationRecord> {
    const response = await apiClient.put<MedicalReservationRecord>(`/api/medical-reservations/${id}`, data);
    return response.data;
  },

  /**
   * 一括ステータス更新
   * @param data 一括更新データ（対象IDと新しいステータス）
   * @returns 更新成功レスポンス
   */
  async bulkUpdateActionStatus(
    data: BulkStatusUpdateRequest
  ): Promise<{ success: boolean; updated_count: number }> {
    const response = await apiClient.put<{ success: boolean; updated_count: number }>(
      '/api/medical-reservations/bulk-status',
      data
    );
    return response.data;
  },

  /**
   * 診療予約レコード削除
   * @param id レコードID
   * @returns 削除成功レスポンス
   */
  async deleteMedicalReservation(id: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete<{ success: boolean }>(`/api/medical-reservations/${id}`);
    return response.data;
  },

  /**
   * CSV詳細データ取得（56列すべて）
   * @param id レコードID
   * @returns CSV全項目データ
   */
  async getCsvDetails(id: string): Promise<{ [key: string]: string }> {
    const response = await apiClient.get<{ [key: string]: string }>(`/api/medical-reservations/${id}/csv-details`);
    return response.data;
  },

  /**
   * 担当医師一覧取得（フィルタ用）
   * @returns 担当医師リスト
   */
  async getDoctors(): Promise<string[]> {
    const response = await apiClient.get<string[]>('/api/medical-reservations/doctors');
    return response.data;
  },

  /**
   * アップロードバッチ一覧取得（フィルタ用）
   * @returns アップロードバッチリスト
   */
  async getUploadBatches(): Promise<Array<{ id: string; name: string; uploaded_at: string }>> {
    const response = await apiClient.get<Array<{ id: string; name: string; uploaded_at: string }>>('/api/medical-reservations/upload-batches');
    return response.data;
  },

  /**
   * スプレッドシートへの貼り付け実行
   * @param reservationIds 貼り付け対象の予約ID配列（文字列型）
   * @returns 貼り付け処理開始レスポンス
   */
  async pasteToSpreadsheet(reservationIds: string[]): Promise<PasteToSpreadsheetResponse> {
    const timer = logger.startTimer('medicalReservationApiService', 'pasteToSpreadsheet');
    
    logger.info('medicalReservationApiService', 'pasteToSpreadsheet:開始', {
      reservationIds,
      count: reservationIds.length
    });

    try {
      // 選択されたレコードから医師コードを抽出（重複排除）
      // TODO: 実際のレコードから医師コードを取得する必要がある場合は、
      // この部分を修正してください
      logger.debug('medicalReservationApiService', 'pasteToSpreadsheet:医師コード抽出', {
        注意: '現在は医師コードの取得ロジックが未実装です。バックエンドAPIで処理されます。'
      });

      // 医師のスプレッドシートURLを事前に取得（将来の実装用）
      // const doctorCodes = Array.from(new Set(records.map(r => r.doctorInCharge)));
      // const spreadsheetUrls = await doctorContractApiService.getMedicalSpreadsheetUrls(doctorCodes);
      
      // リクエストボディの構築
      const requestBody = { 
        reservation_ids: reservationIds,
        // 将来的にはここにspreadsheet_urlsを追加する可能性がある
        // spreadsheet_urls: Object.fromEntries(spreadsheetUrls)
      };
      
      logger.debug('medicalReservationApiService', 'pasteToSpreadsheet:APIリクエスト', {
        url: '/medical-reservations/paste-to-spreadsheet',
        method: 'POST',
        bodyKeys: Object.keys(requestBody),
        reservationCount: reservationIds.length
      });
      
      const response = await apiClient.post<PasteToSpreadsheetResponse>(
        '/api/medical-reservations/paste-to-spreadsheet',
        requestBody
      );
      
      logger.info('medicalReservationApiService', 'pasteToSpreadsheet:成功', {
        success: response.data.success,
        jobId: response.data.data?.job_id,
        totalCount: response.data.data?.total_count,
        doctors: response.data.data?.doctors?.map(d => ({
          code: d.doctor_code,
          name: d.doctor_name,
          count: d.record_count,
          hasUrl: d.spreadsheet_url !== null
        }))
      });
      
      timer({
        success: true,
        reservationCount: reservationIds.length,
        doctorCount: response.data.data?.doctors?.length || 0
      });
      
      return response.data;
    } catch (error: any) {
      logger.error('medicalReservationApiService', 'pasteToSpreadsheet:エラー', error, {
        reservationIds,
        status: error.response?.status,
        message: error.response?.data?.message || error.message
      });
      
      timer({
        success: false,
        error: error.message
      });
      
      throw error;
    }
  },

  /**
   * 貼り付け処理のステータス確認
   * @param jobId ジョブID
   * @returns 処理ステータス
   */
  async getPasteStatus(jobId: string): Promise<PasteStatusResponse['data']> {
    const response = await apiClient.get<PasteStatusResponse>(
      `/api/medical-reservations/paste-to-spreadsheet/${jobId}/status`
    );
    return response.data.data;
  },

  /**
   * 貼り付け処理の進捗をポーリング
   * @param jobId ジョブID
   * @param onProgress 進捗更新時のコールバック
   * @param interval ポーリング間隔（ミリ秒）
   * @returns 最終的な処理結果
   */
  async pollPasteStatus(
    jobId: string,
    onProgress?: (status: PasteStatusResponse['data']) => void,
    interval: number = 2000
  ): Promise<PasteStatusResponse['data']> {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const response = await this.getPasteStatus(jobId);
          
          const statusData = response;
          
          // 進捗コールバックを実行
          if (onProgress) {
            onProgress(statusData);
          }
          
          // 処理が完了または失敗した場合
          if (statusData?.status === 'completed' || statusData?.status === 'failed') {
            resolve(statusData);
          } else {
            // まだ処理中の場合は再度ポーリング
            setTimeout(poll, interval);
          }
        } catch (error) {
          console.error('pollPasteStatus - エラー:', error);
          reject(error);
        }
      };
      
      // 初回実行
      poll();
    });
  },

};

/**
 * モックデータ生成関数（開発・テスト用）
 */
export const createMockMedicalReservations = (): MedicalReservationRecord[] => {
  const mockCsvData = {
    '成果報酬': '',
    'カウンセラー対応': 'なし',
    '担当医師': '田中医師',
    'anybot ID': 'TEST001',
    '診療開始時間': '2025-07-02 10:15:00',
    'tags': '',
    'ステータス': '',
    '会員登録': '○',
    '問診回答': '○',
    '再診用問診': '回答済み',
    '電話番号': '090-1111-1111',
    '診療スタイル': 'オンライン',
    '使用ツール': 'Zoom',
    '名前（漢字）': 'テスト花子',
    '名前（カナ）': 'テストハナコ',
    '診療リクエスト': '再診相談',
    '遅延': 'なし',
    '診療対応': '処方検討中',
    '中用量処方有無': 'なし',
    'アフターピル処方有無': 'なし',
    '副作用改善薬処方有無': 'なし',
    'CS対応必要有無': 'なし',
    '医師からCS/カウンセラー': '',
    'Wチェック': '○',
    'mederi CSから医師へ': '',
    'カウンセラーから医師へ': '',
    '年齢': '25',
    '親への同意': '○',
    '服用経験': 'あり',
    '服用していたピル': 'トリキュラー',
    '改善目的': '副作用緩和',
    '希望ピル': 'マーベロン',
    '医師に聞きたいこと': '副作用について',
    '※再診問診 医師にききたいこと': '',
    '※再診問診 種類変更希望': '',
    '※再診問診 副作用緩和薬希望': '○',
    '前回生理開始日': '2024-12-01',
    '月経周期': '28',
    '生理を避けたい日にち': '年末年始',
    '性行為の日時': '',
    '妊娠希望': 'なし',
    '妊娠経験': 'なし',
    '妊娠時期・出産回数': '',
    '妊娠・出産・授乳有無': 'なし',
    '過去妊娠中に「黄疸」「ヘルペス」「持続性掻疼症」と診断されたことはありますか？': 'なし',
    '過去2週間以内に手術': 'なし',
    '前兆のある片頭痛': 'なし',
    '飲んでいるお薬': 'なし',
    'アレルギー': '花粉症',
    '身長': '160',
    '体重': '50',
    'BMIの値は30以下': '○',
    '高血圧診断': 'なし',
    'タバコ': 'なし',
    '不正出血': 'なし',
    '婦人科系疾患': 'なし',
    'その他疾患': 'なし',
    'ご家族に血栓症': 'なし',
    'ご家族に乳がん': 'なし',
    '身分証': '運転免許証',
    '採血画像': 'なし',
  };

  return [
    {
      id: '1',
      upload_batch_id: 'batch_001',
      doctorInCharge: '田中医師',
      anybotId: 'TEST001',
      patientName: 'テスト花子',
      consultationStartTime: '2025-07-02 10:15:00',
      actionStatus: 'pending' as ActionStatus,
      csvData: mockCsvData,
      created_at: '2025-07-02T10:15:00Z',
      updated_at: '2025-07-02T10:15:00Z',
    },
    {
      id: '2',
      upload_batch_id: 'batch_001',
      doctorInCharge: '佐藤医師',
      anybotId: 'TEST002',
      patientName: 'テスト美香',
      consultationStartTime: '2025-07-02 11:10:00',
      actionStatus: 'assigned' as ActionStatus,
      assignedTo: '看護師A',
      csvData: { ...mockCsvData, 'anybot ID': 'TEST002', '名前（漢字）': 'テスト美香', '担当医師': '佐藤医師' },
      created_at: '2025-07-02T11:10:00Z',
      updated_at: '2025-07-02T11:10:00Z',
    },
    {
      id: '3',
      upload_batch_id: 'batch_001',
      doctorInCharge: '山田医師',
      anybotId: 'TEST003',
      patientName: 'テスト桜',
      consultationStartTime: '2025-07-02 14:00:00',
      actionStatus: 'pending' as ActionStatus,
      csvData: { ...mockCsvData, 'anybot ID': 'TEST003', '名前（漢字）': 'テスト桜', '担当医師': '山田医師' },
      created_at: '2025-07-02T14:00:00Z',
      updated_at: '2025-07-02T14:00:00Z',
    },
    {
      id: '4',
      upload_batch_id: 'batch_002',
      doctorInCharge: '田中医師',
      anybotId: 'TEST004',
      patientName: 'テスト太郎',
      consultationStartTime: '2025-07-02 15:08:00',
      actionStatus: 'assigned' as ActionStatus,
      assignedTo: '看護師B',
      csvData: { ...mockCsvData, 'anybot ID': 'TEST004', '名前（漢字）': 'テスト太郎', '担当医師': '田中医師' },
      created_at: '2025-07-02T15:08:00Z',
      updated_at: '2025-07-02T15:08:00Z',
    },
    {
      id: '5',
      upload_batch_id: 'batch_002',
      doctorInCharge: '佐藤医師',
      anybotId: 'TEST005',
      patientName: 'テスト愛',
      consultationStartTime: '2025-07-02 16:45:00',
      actionStatus: 'pending' as ActionStatus,
      csvData: { ...mockCsvData, 'anybot ID': 'TEST005', '名前（漢字）': 'テスト愛', '担当医師': '佐藤医師' },
      created_at: '2025-07-02T16:45:00Z',
      updated_at: '2025-07-02T16:45:00Z',
    },
  ];
};

export default medicalReservationApiService;